/* back-in-stock-link.js
 * Ties a "Back in stock" email signup to OUR visitor_id.
 *
 * The back-in-stock widget is the third-party `amp-back-in-stock` Shopify APP
 * block — its email goes to the app, and the app has no access to our visitor_id
 * (it lives in our localStorage). This tiny interceptor watches for the shopper
 * submitting that widget, reads the email they typed + our visitorID, and
 * fire-and-forget POSTs {email, vid, source} to a GCP Cloud Run sink, which
 * streams ONE row into BigQuery company-data-hub-497311.users.users_drip — so
 * the email is tied to OUR visitor_id. It NEVER blocks or alters the app's own
 * submit.
 *
 * Config:
 *   - window.AGB_VID_EMAIL_URL — REQUIRED: the deployed Cloud Run endpoint
 *     (…run.app/users-drip). Until it's set, this no-ops (attaches nothing).
 *   - window.AGB_BACK_IN_STOCK_SELECTOR — optional: a CSS selector for the
 *     widget's submit/form if the heuristic misses (grab it from DevTools).
 */
(function () {
  'use strict';

  // GCP Cloud Run sink -> BigQuery company-data-hub-497311.users.users_drip.
  // Configured per store via window.AGB_VID_EMAIL_URL (set in theme.liquid).
  var ENDPOINT = (typeof window !== 'undefined' && window.AGB_VID_EMAIL_URL) || '';
  if (!ENDPOINT) return;                 // not configured yet → no-op (no errors)
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var sent = {};   // email -> true (dedupe per page session)

  function visitorId() {
    try { return localStorage.getItem('visitorID') || null; } catch (e) { return null; }
  }

  // Walk up from the clicked/submitted element to the back-in-stock widget. Match
  // the amp-back-in-stock app block, an explicit override selector, or a
  // container/button whose id/class/text mentions stock/availability/notify.
  function backInStockBox(el) {
    var override = (typeof window !== 'undefined') && window.AGB_BACK_IN_STOCK_SELECTOR;
    for (var node = el; node && node !== document; node = node.parentElement) {
      try {
        if (override && node.matches && node.matches(override)) return node;
        var id  = (node.id || '').toLowerCase();
        var cls = (node.className && node.className.toString) ? node.className.toString().toLowerCase() : '';
        if (/amp-back-in-stock|back.?in.?stock|backinstock|\bbis\b|restock|notify.?me|notify-?when/.test(id + ' ' + cls)) return node;
        if (node.matches && node.matches('[id^="shopify-block-"], [data-block-handle]')) {
          var t = (node.textContent || '').toLowerCase();
          if (/back in stock|when available|notify me|email when available|restock/.test(t)) return node;
        }
      } catch (e) { /* ignore */ }
    }
    return null;
  }

  function validEmailIn(container) {
    if (!container || !container.querySelector) return null;
    var inp = container.querySelector('input[type="email"], input[name*="email" i], input[autocomplete="email"]');
    var v = (inp && inp.value) ? inp.value.trim() : '';
    return EMAIL_RE.test(v) ? v : null;
  }

  function capture(targetEl) {
    var box = backInStockBox(targetEl);
    if (!box) return;
    var email = validEmailIn(box);
    if (!email || sent[email]) return;
    var vid = visitorId();
    if (!vid) return;                       // nothing to tie it to
    // SAFETY GATE (session-guard.js) — identity-spine write; denied for every
    // non-shopper session. Fails closed.
    try {
      if (window.SE_Session) {
        if (!window.SE_Session.allow('identity', 'back_in_stock')) return;
      } else {
        // Guard asset missing (404 / parse error / not yet pushed). An "assumed"
        // session is IMPOSSIBLE without it — begin() lives in the guard — so the
        // legacy prefix check is a COMPLETE substitute here, and a real shopper
        // is never broken by a missing file. Fail-safe, not fail-dark.
        var _gv = ''; try { _gv = localStorage.getItem('visitorID') || ''; } catch (e2) {}
        if (_gv.indexOf('facade00-') === 0 || _gv.indexOf('staff00-') === 0) return;
      }
    } catch (e) { /* never block a real shopper on the guard itself */ }
    sent[email] = true;
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, vid: vid, source: 'back_in_stock' }),
        credentials: 'omit',                // backend runs allow_credentials=False
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* fire-and-forget */ }
  }

  // Capture phase so we still see it if the app stops propagation. The email
  // input still holds its value at click/submit time (before the app clears it).
  document.addEventListener('submit', function (e) { capture(e.target); }, true);
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('button, a, [type="submit"], [role="button"]');
    if (btn) capture(btn);
  }, true);
})();
